import React, { useState } from "react";
import { IS_VATAR } from "../vatar/env";
import PowerUpShop from "../Components/PowerUpShop";
import CoinStore from "../Components/CoinStore";
import useCoinStore from "../stores/coinStore";
import { Zap, Eye, Shield, Hand, Scale as ScaleIcon } from "lucide-react";
import { MascotAvatar, PencilMascot, useMyMascot } from "../Components/Mascot";

const RoundResults = ({ roomData, roomDocId, currentRound, onContinue, desktopActions }) => {
  const [showPowerUpShop, setShowPowerUpShop] = useState(false);
  const myMascot = useMyMascot();
  const [showCoinStore, setShowCoinStore] = useState(false);
  const [revealedVotes, setRevealedVotes] = useState({});
  const [voteRevealProcessing, setVoteRevealProcessing] = useState(null);
  const { canAfford, spendCoins } = useCoinStore();
  if (!roomData?.rounds?.[currentRound]) {
    return <div>Loading results...</div>;
  }

  const roundData = roomData.rounds[currentRound];
  const submissions = roundData.submissions || [];
  const scores = roundData.scores || {};
  const letter = roundData.letter;

  // Get categories from roomData or use default
  const categories = roomData.categories || [
    "name",
    "animal",
    "place",
    "thing",
  ];

  // Get player details with scores
  const playerResults = roomData.players.map((player) => {
    const submission = submissions.find((sub) => sub.userId === player.id);

    // Create a default score object with all categories set to 0
    const defaultScores = { total: 0 };
    categories.forEach((category) => {
      defaultScores[category] = 0;
    });

    // Get actual scores or use defaults
    const playerScores = scores[player.id] || defaultScores;

    return {
      ...player,
      submission: submission?.answers || {},
      scores: playerScores,
    };
  });

  // Calculate overall standings (sum of all completed rounds + power-up adjustments)
  const getOverallStandings = () => {
    const playerTotals = {};

    roomData.players.forEach((player) => {
      playerTotals[player.id] = { name: player.name, total: 0 };
    });

    Object.values(roomData.rounds).forEach((round) => {
      if (round.scores) {
        Object.entries(round.scores).forEach(([userId, categoryScores]) => {
          if (playerTotals[userId]) {
            playerTotals[userId].total += categoryScores.total || 0;
          }
        });
      }
      // Include power-up cumulative adjustments (steal_points, share_fate)
      if (round.powerUpScores) {
        Object.entries(round.powerUpScores).forEach(([userId, adjustment]) => {
          if (playerTotals[userId]) {
            playerTotals[userId].total += adjustment || 0;
          }
        });
      }
    });

    return Object.values(playerTotals).sort((a, b) => b.total - a.total);
  };

  const overallStandings = getOverallStandings();

  const reviewVotes = roundData.reviewVotes || {};

  const handleRevealVotes = async (playerId, category) => {
    const key = `${playerId}-${category}`;
    if (revealedVotes[key]) return;
    const voteData = reviewVotes[category]?.[playerId] || {};
    // Only allow reveal if per-voter data was stored (games after vote-reveal feature launch)
    if (!voteData.validVoters && !voteData.invalidVoters) return;
    if (!canAfford(10)) {
      setShowCoinStore(true);
      return;
    }
    setVoteRevealProcessing(key);
    const result = await spendCoins(10, "vote_reveal", roomDocId);
    setVoteRevealProcessing(null);
    if (!result.success) return;
    const validVoterIds = voteData.validVoters || [];
    const invalidVoterIds = voteData.invalidVoters || [];
    const getName = (id) => roomData.players.find((p) => p.id === id)?.name || id;
    setRevealedVotes((prev) => ({
      ...prev,
      [key]: {
        valid: validVoterIds.map(getName),
        invalid: invalidVoterIds.map(getName),
      },
    }));
  };

  // Helper function to get special round info with enhanced styling
  const getSpecialRoundInfo = () => {
    const specialRounds = roomData?.specialRounds || [];
    const currentSpecialRound = specialRounds.find(
      (sr) => sr.round === currentRound
    );

    if (!currentSpecialRound) return null;

    // Check what type of special effect happened
    const playersWithEffects = playerResults.filter(
      (player) => player.scores.specialEffect
    );

    if (playersWithEffects.length === 0) return null;

    // Define effect styling and headers based on type
    const getEffectConfig = (type) => {
      switch (type) {
        case "first_double":
          return {
            title: "First Submission Bonus",
            colorClass:
              "from-green-100 to-emerald-100 border-green-300 text-green-800",
            headerColor: "text-green-700",
            isGlobal: false,
          };
        case "first_half":
          return {
            title: "Early Submission Penalty",
            colorClass: "from-red-100 to-rose-100 border-red-300 text-red-800",
            headerColor: "text-red-700",
            isGlobal: false,
          };
        case "last_double":
          return {
            title: "Last Submission Bonus",
            colorClass:
              "from-green-100 to-emerald-100 border-green-300 text-green-800",
            headerColor: "text-green-700",
            isGlobal: false,
          };
        case "last_half":
          return {
            title: "Late Submission Penalty",
            colorClass: "from-red-100 to-rose-100 border-red-300 text-red-800",
            headerColor: "text-red-700",
            isGlobal: false,
          };
        case "time_zones":
          return {
            title: "Speed Round",
            colorClass:
              "from-orange-100 to-amber-100 border-orange-300 text-orange-800",
            headerColor: "text-orange-700",
            isGlobal: false,
          };
        case "point_thief":
          return {
            title: "Point Thief",
            colorClass:
              "from-purple-100 to-violet-100 border-purple-300 text-purple-800",
            headerColor: "text-purple-700",
            isGlobal: false,
          };
        case "equality":
        case "round_equality":
          return {
            title: "Round Equality",
            colorClass:
              "from-purple-100 to-indigo-100 border-purple-300 text-purple-800",
            headerColor: "text-purple-700",
            isGlobal: true,
            globalMessage: "Everyone gets the same score this round",
          };
        case "total_equality_average":
          return {
            title: "Average Equality",
            colorClass:
              "from-purple-100 to-indigo-100 border-purple-300 text-purple-800",
            headerColor: "text-purple-700",
            isGlobal: true,
            globalMessage: "All total scores have been averaged",
          };
        case "total_equality_random":
          // Extract player name from the special effect message
          const randomPlayer = playersWithEffects.find((p) =>
            p.scores.specialEffect?.includes("Everyone gets")
          );
          const playerName =
            randomPlayer?.scores.specialEffect?.match(
              /Everyone gets (.+)'s score!/
            )?.[1] || "a player";
          return {
            title: "Random Equality",
            colorClass:
              "from-purple-100 to-indigo-100 border-purple-300 text-purple-800",
            headerColor: "text-purple-700",
            isGlobal: true,
            globalMessage: `Everyone now has ${playerName}'s score by random chance`,
          };
        case "chosen_one":
          return {
            title: "Chosen One",
            colorClass:
              "from-yellow-100 to-amber-100 border-yellow-300 text-yellow-800",
            headerColor: "text-yellow-700",
            isGlobal: false,
          };
        default:
          return {
            title: "Special Effect",
            colorClass:
              "from-gray-100 to-slate-100 border-gray-300 text-gray-800",
            headerColor: "text-gray-700",
            isGlobal: false,
          };
      }
    };

    const config = getEffectConfig(currentSpecialRound.type);

    return {
      type: currentSpecialRound.type,
      config,
      players: playersWithEffects,
    };
  };

  const specialRoundInfo = getSpecialRoundInfo();

  // Power-up effects that should be publicly announced this round
  const announcedPowerUpEffects = (
    roundData.powerUpEffectsLog || []
  ).filter((e) => e.announced && e.round === currentRound);

  return (
    <div className="min-h-scree bg-orange-50 container px-6 pt-8">
      <div className="max-w-4xl lg:max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <PencilMascot mood="happy" size={44} label="Namey" />
            <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-red-800">
              Round {currentRound} Results
            </h1>
            <p className="text-sm sm:text-base text-gray-700">
              Letter: <span className="font-bold text-red-600">{letter}</span>
            </p>
            </div>
          </div>
          {/* Just a bolt. Coins and buying coins live inside the shop, where they matter. */}
          <button
            onClick={() => setShowPowerUpShop(true)}
            className={`shrink-0 items-center justify-center rounded-full p-2 text-red-600 transition-colors hover:bg-red-100 ${IS_VATAR ? "hidden" : "flex"}`}
            aria-label="Power-Up Shop"
            title="Power-Up Shop"
          >
            <Zap className="h-6 w-6" />
          </button>
        </div>

        {/* Enhanced Special Round Reveal */}
        {specialRoundInfo && (
          <div
            className={`relative bg-gradient-to-r ${specialRoundInfo.config.colorClass} border-2 rounded-lg p-6 mb-8`}
          >
            <div className="absolute right-3 top-3" aria-hidden="true">
              <MascotAvatar mascot={myMascot} mood="celebrate" size={myMascot.base === "pencil" ? 34 : 50} shadow={false} />
            </div>
            <div className="text-center mb-2">
              <h2 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
                Special Round
              </h2>
              <h3
                className={`text-2xl font-bold ${specialRoundInfo.config.headerColor}`}
              >
                {specialRoundInfo.config.title}
              </h3>
            </div>

            <div className="text-center">
              {specialRoundInfo.config.isGlobal ? (
                // Global effects - show general message
                <p className="text-base">
                  {specialRoundInfo.config.globalMessage}
                </p>
              ) : (
                // Individual effects - show per player
                <div className="space-y-3">
                  {specialRoundInfo.players.map((player) => (
                    <div key={player.id} className="text-lg">
                      <span className="font-semibold">{player.name}</span>
                      <span className="mx-2">-</span>
                      <span className="text-base">
                        {player.scores.specialEffect}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Power-Up Effects */}
        {announcedPowerUpEffects.length > 0 && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center">
                <Eye className="w-3.5 h-3.5 text-white" />
              </div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                Behind the Scenes
              </h3>
            </div>
            <div className="space-y-2">
              {announcedPowerUpEffects.map((effect, i) => {
                if (effect.outcome === "blocked_by_shield") {
                  return (
                    <div key={i} className="flex items-center gap-3 bg-blue-50 rounded-lg px-3 py-2.5">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-4 h-4 text-blue-600" />
                      </div>
                      <p className="text-sm text-blue-800 font-medium">
                        <span className="font-bold">{effect.targetName}'s</span> shield blocked an attack!
                      </p>
                    </div>
                  );
                }
                if (effect.outcome === "refunded_special_round") return null;
                if (effect.type === "steal_points") {
                  return (
                    <div key={i} className="flex items-center gap-3 bg-red-50 rounded-lg px-3 py-2.5">
                      <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                        <Hand className="w-4 h-4 text-red-600" />
                      </div>
                      <p className="text-sm text-red-800 font-medium">
                        <span className="font-bold">{effect.sourceName}</span> stole {effect.amount} pts from <span className="font-bold">{effect.targetName}</span>
                      </p>
                    </div>
                  );
                }
                if (effect.type === "share_fate") {
                  return (
                    <div key={i} className="flex items-center gap-3 bg-amber-50 rounded-lg px-3 py-2.5">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <ScaleIcon className="w-4 h-4 text-amber-600" />
                      </div>
                      <p className="text-sm text-amber-800 font-medium">
                        <span className="font-bold">{effect.sourceName}</span> & <span className="font-bold">{effect.targetName}</span> split scores — both at {effect.newSharedTotal} pts
                      </p>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}



        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-6 lg:items-start">
        {/* Round Results */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8 min-w-0">
          <h2 className="text-xl font-semibold mb-4 text-center">
            Round Scores
          </h2>

          {/* Results Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-2">Player</th>
                  {/* Dynamically create table headers based on categories */}
                  {categories.map((category) => (
                    <th
                      key={category}
                      className="text-center py-3 px-2 capitalize"
                    >
                      {category}
                    </th>
                  ))}
                  <th className="text-center py-3 px-2 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {playerResults.map((player, index) => (
                  <tr key={player.id} className="border-b border-gray-100">
                    <td className="py-3 px-2 font-semibold">{player.name}</td>
                    {/* Dynamically create table cells based on categories */}
                    {categories.map((category) => {
                      const revealKey = `${player.id}-${category}`;
                      const revealed = revealedVotes[revealKey];
                      const isProcessing = voteRevealProcessing === revealKey;
                      const hasVotes = reviewVotes[category]?.[player.id]?.voters?.length > 0;
                      return (
                        <td key={category} className="text-center py-3 px-2">
                          <div className="space-y-1">
                            <div className="text-xs text-gray-600">
                              {player.submission[category] || ""}
                            </div>
                            <div
                              className={`font-bold ${player.scores[category] >= 10
                                ? "text-green-600"
                                : player.scores[category] === 5
                                  ? "text-orange-600"
                                  : "text-red-600"
                                }`}
                            >
                              {player.scores[category] || 0}
                            </div>
                            {roundData.coinFlips?.[player.id]?.includes(category) && (
                              <div className="text-[11px] leading-tight text-gray-600">
                                No clear vote, coin flip
                              </div>
                            )}
                            {/* Vote reveal UI — commented out until redesigned for mobile
                            {hasVotes && !revealed && (
                              <button
                                onClick={() => handleRevealVotes(player.id, category)}
                                disabled={isProcessing}
                                className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-0.5 mx-auto"
                              >
                                <Eye size={10} />
                                {isProcessing ? "..." : "10 🪙"}
                              </button>
                            )}
                            {revealed && (
                              <div className="text-xs mt-1 space-y-0.5">
                                {revealed.valid.length > 0 && (
                                  <div className="text-green-600">✓ {revealed.valid.join(", ")}</div>
                                )}
                                {revealed.invalid.length > 0 && (
                                  <div className="text-red-500">✗ {revealed.invalid.join(", ")}</div>
                                )}
                              </div>
                            )}
                            */}
                          </div>
                        </td>
                      );
                    })}
                    <td className="text-center py-3 px-2">
                      <div className="space-y-1">
                        <span className="font-bold text-lg text-red-600">
                          {player.scores.total ?? 0}
                        </span>
                        {(player.scores.specialBonus > 0 ||
                          player.scores.specialBonus < 0) && (
                            <div
                              className={`text-xs font-bold ${player.scores.specialBonus > 0
                                ? "text-green-600"
                                : "text-red-600"
                                }`}
                            >
                              {player.scores.specialBonus > 0 ? "+" : ""}
                              {player.scores.specialBonus}
                            </div>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Score Legend */}
          <div className="mt-4 text-xs text-center text-gray-600">
            <span className="text-green-600 font-semibold">10 points</span> =
            Unique answer •{" "}
            <span className="text-orange-600 font-semibold">5 points</span> =
            Shared answer •{" "}
            <span className="text-red-600 font-semibold">0 points</span> =
            Invalid/No answer
          </div>
        </div>

        <div>
        {/* Overall Standings */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8 lg:mb-4">
          <h2 className="text-xl font-semibold mb-4 text-center">
            Overall Standings
          </h2>
          <div className="max-w-md mx-auto">
            {overallStandings.map((player, index) => (
              <div
                key={player.name}
                className="flex justify-between items-center py-3 px-4 border-b border-gray-100"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">
                    {index === 0
                      ? "🥇"
                      : index === 1
                        ? "🥈"
                        : index === 2
                          ? "🥉"
                          : `${index + 1}.`}
                  </span>
                  <span className="font-semibold">{player.name}</span>
                </div>
                <span className="text-xl font-bold text-red-600">
                  {player.total}
                </span>
              </div>
            ))}
          </div>
        </div>

        {desktopActions && <div className="hidden lg:block mb-8">{desktopActions}</div>}
        </div>
        </div>

        {/* Continue Button */}
        {/* <div className="text-center mb-8">
          <button
            onClick={onContinue}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors"
          >
            Continue to Next Round
          </button>
        </div> */}
      </div>

      {showCoinStore && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <CoinStore isOpen={true} onClose={() => setShowCoinStore(false)} />
          </div>
        </div>
      )}
      {showPowerUpShop && (
        <PowerUpShop
          roomData={roomData}
          roomDocId={roomDocId}
          currentRound={currentRound}
          onClose={() => setShowPowerUpShop(false)}
        />
      )}
    </div>
  );
};

export default RoundResults;
